import * as React from 'react';

import type {
	W_FieldReport_FindAllReportTypesForCompany_VM,
	W_FieldReport_FindAllReportTypesForCompany_VM_Row as ReportTypeRow,
	W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock,
	W_FieldReport_FindAllReportTypesForCompany_VM_ReportBlockField,
} from 'ab-api/web/fieldReport/findAllReportTypesForCompany';

import { revisionCode } from '@acceligentllc/shared/utils/workOrderRevision';

import { Button } from '@acceligentllc/storybook';

import Radio from 'af-components/Controls/Radio';
import CustomModal from 'af-components/CustomModal';
import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';
import { selectableFieldTypes } from '../AddFieldsToColumnsModal/ReportTypeBlocks';
import type { ColumnDataSourceFM, TemplateColumnFM } from '../Shared/formModel';
import { getBlockGroups, indexReportTypes, withCidDS } from '../Shared/helpers';

interface Props {
	show: boolean;
	onClose: () => void;
	onApply: (patch: {
		addSelections: ColumnDataSourceFM[];
		ignoreRevisionIds: number[];
	}) => void;
	companyFieldReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>;
	latestReportTypeId: number;
	column: TemplateColumnFM;
}

type SelectionByRt = Map<number, { rbId: number; rbfId: number; }>;
type IgnoreList = number[];

const TOOLTIP_MESSAGE = <div className={styles.tooltip}>
	<b>Mark all as Do not use</b>
	Applies "Do not use" to all revisions that neither have a data source nor are selected as "Do not use".
</div>;

const TOOLTIP_MESSAGE_WARNING = <div className={styles.tooltip}>
	<b>Missing Association</b>
	This revision has no associated field. Please select a field or mark it as "Do not use" to continue.
</div>;

const ReportTypeRevisionsModal: React.FC<Props> = (props) => {
	const { show, onClose, onApply, companyFieldReportTypes, latestReportTypeId, column } = props;

	const { byId: reportTypesById, byFamily: reportTypesByFamily } = React.useMemo(() => indexReportTypes(companyFieldReportTypes), [companyFieldReportTypes]);
	const latestRt = React.useMemo(() => reportTypesById.get(latestReportTypeId), [reportTypesById, latestReportTypeId]);
	const familyKey = React.useMemo(() => latestRt ? latestRt.familyKey : null, [latestRt]);
	const familyChain = React.useMemo(() => (
		familyKey
			? (reportTypesByFamily.get(familyKey) ?? [])
			: []
	), [reportTypesByFamily, familyKey]);

	// Current draft selections/exclusions scoped to this family
	const initialSelectionsByReportTypeRevision = React.useMemo(() => {
		const idsOfFamily = familyChain.map((rt) => rt.id);

		return column.dataSources.reduce<SelectionByRt>((map, dataSource) => {
			const field = dataSource.field;
			if (!field || !idsOfFamily.includes(field.reportTypeId)) {
				return map;
			}

			map.set(
				field.reportTypeId,
				{
					rbId: field.reportBlockId,
					rbfId: field.reportBlockFieldId,
				}
			);

			return map;
		}, new Map());
	}, [column.dataSources, familyChain]);

	const initiallyIgnored = React.useMemo(() => {
		const familyIds = familyChain.map((rt) => rt.id);

		return column.excludedFieldReportIds.reduce<IgnoreList>((acc, id) => {
			if (familyIds.includes(id)) {
				acc.push(id);
			}
			return acc;
		}, []);
	}, [column.excludedFieldReportIds, familyChain]);

	const [selectionsByRt, setSelectionsByRt] = React.useState<SelectionByRt>(initialSelectionsByReportTypeRevision);
	const [ignoreList, setIgnoreList] = React.useState<IgnoreList>(initiallyIgnored);
	const [filterUnassigned, setFilterUnassigned] = React.useState<boolean>(true);

	React.useEffect(() => {
		setSelectionsByRt(initialSelectionsByReportTypeRevision);
		setIgnoreList(initiallyIgnored);
	}, [initialSelectionsByReportTypeRevision, initiallyIgnored, show]);

	const onPickField = React.useCallback((rtId: number, rbId: number, rbfId: number) => {
		setSelectionsByRt((prev) => {
			const next = new Map(prev);
			next.set(rtId, { rbId, rbfId });
			return next;
		});
		setIgnoreList((prev) => {
			if (!prev.includes(rtId)) {
				return prev;
			}
			return prev.filter((id) => id !== rtId);
		});
	}, []);

	const onToggleIgnore = React.useCallback((rtId: number, checked: boolean) => {
		setIgnoreList((prev) => {
			if (checked) {
				if (!prev.includes(rtId)) {
					return [...prev, rtId];
				}
				return prev;
			}
			if (prev.includes(rtId)) {
				return prev.filter((id) => id !== rtId);
			}
			return prev;
		});
		if (checked) {
			setSelectionsByRt((prev) => {
				if (!prev.has(rtId)) {
					return prev;
				}
				const next = new Map(prev);
				next.delete(rtId);
				return next;
			});
		}
	}, []);

	// Used for validation: every unresolved revision must be selected or ignored
	const unresolvedRtIds = React.useMemo(() => {
		const excludedOnPurpose = column.excludedFieldReportIds ?? [];

		return familyChain.reduce<number[]>((acc, reportType) => {
			const hasSelection = selectionsByRt.has(reportType.id);
			const ignored = ignoreList.includes(reportType.id);
			const excluded = excludedOnPurpose.includes(reportType.id);

			if (!hasSelection && !ignored && !excluded) {
				acc.push(reportType.id);
			}
			return acc;
		}, []);
	}, [familyChain, selectionsByRt, ignoreList, column.excludedFieldReportIds]);

	const canSave = React.useMemo(() => unresolvedRtIds.length === 0, [unresolvedRtIds]);

	const onSave = React.useCallback(() => {
		// Build patch limited to this family
		const addSelections: ColumnDataSourceFM[] = [];
		selectionsByRt.forEach(({ rbId, rbfId }, rtId) => {
			addSelections.push(
				withCidDS({
					field: { reportTypeId: rtId, reportBlockId: rbId, reportBlockFieldId: rbfId },
				})
			);
		});

		onApply({
			addSelections,
			ignoreRevisionIds: ignoreList,
		});
	}, [onApply, selectionsByRt, ignoreList]);

	const handleFieldSelect = React.useCallback((rtId: number, rbId: number, rbfId: number) => () => {
		onPickField(rtId, rbId, rbfId);
	}, [onPickField]);

	const handleMarkAssignedAsDoNotUse = React.useCallback(() => {
		setIgnoreList((prev) => {
			const next = [...prev];

			familyChain.forEach((reportType) => {
				const hasSelection = selectionsByRt.has(reportType.id);
				const ignored = next.includes(reportType.id);
				const excluded = column.excludedFieldReportIds.includes(reportType.id);

				if (!hasSelection && !ignored && !excluded) {
					next.push(reportType.id);
				}
			});

			return next;
		});
	}, [familyChain, selectionsByRt, column.excludedFieldReportIds]);

	const isMarkAssignedAsDoNotUseDisabled = React.useMemo(() => {
		return !familyChain.some((reportType) => {
			const hasSelection = selectionsByRt.has(reportType.id);
			const ignored = ignoreList.includes(reportType.id);
			const excluded = column.excludedFieldReportIds.includes(reportType.id);

			return !hasSelection && !ignored && !excluded;
		});
	}, [familyChain, selectionsByRt, ignoreList, column.excludedFieldReportIds]);

	const renderBlockField = React.useCallback(
		(rt: ReportTypeRow, rbId: number, isPrimary: boolean) => (bf: W_FieldReport_FindAllReportTypesForCompany_VM_ReportBlockField) => {
			const selected = selectionsByRt.get(rt.id);
			const checked = !!selected && selected.rbId === rbId && selected.rbfId === bf.id;

			const blockFieldClassName = styles[`blockField__${isPrimary ? 'primary' : 'secondary'}`];
			return (
				<div className={blockFieldClassName} key={bf.id}>
					<div className={styles['block-field-radio']}>
						<Radio
							checked={checked}
							label={bf.name}
							onCheck={handleFieldSelect(rt.id, rbId, bf.id)}
						/>
					</div>
				</div>
			);
		},
		[selectionsByRt, handleFieldSelect]
	);

	const renderBlock = React.useCallback((
		rt: ReportTypeRow,
		block: W_FieldReport_FindAllReportTypesForCompany_VM_ReportTypeBlock,
		isMain: boolean,
		isPrimary: boolean
	) => {
		const blockClassName = styles[`block__${isPrimary ? 'primary' : 'secondary'}`];

		const blockHeaderClassName = styles[`block-header__${isMain ? 'main' : 'secondary'}`];

		const filteredFields = block.reportBlock.fields.filter(
			(field) => selectableFieldTypes.includes(field.fieldType));

		if (!filteredFields.length) {
			return null;
		}

		return (
			<div className={blockClassName}>
				<div className={blockHeaderClassName}>
					{block.reportBlock.name}
				</div>
				<div className={styles['block-fields']}>
					{block.reportBlock.fields.map(renderBlockField(rt, block.reportBlockId, isPrimary))}
				</div>
			</div>
		);
	}, [renderBlockField]);

	const renderRevision = React.useCallback(
		(rt: ReportTypeRow) => {

			const {
				upperTotal,
				primaryMain,
				primaryOther,
				secondaryMain,
				secondaryOther,
				lowerTotal,
			} = getBlockGroups(rt);

			return (
				<div>
					{upperTotal &&
						<div className={styles['primary-blocks']}>{
							renderBlock(rt, upperTotal, true, true)}
						</div>
					}
					<div className={styles['primary-blocks']}>
						{primaryMain && renderBlock(rt, primaryMain, true, true)}
						{primaryOther.map((rb) => renderBlock(rt, rb, false, true))}
					</div>
					{
						secondaryMain && <div className={styles['secondary-blocks']}>
							{secondaryMain && renderBlock(rt, secondaryMain, true, false)}
							{secondaryOther.map((rb) => renderBlock(rt, rb, false, false))}
						</div>
					}
					{lowerTotal &&
						<div className={styles['primary-blocks']}>{renderBlock(rt, lowerTotal, true, true)}
						</div>
					}
				</div>
			);
		},
		[renderBlock]
	);

	const filteredChain = React.useMemo(() => {
		if (!filterUnassigned) {
			return familyChain;
		}
		return familyChain.filter((rt) => {
			const hasSelection = selectionsByRt.has(rt.id);
			const ignored = ignoreList.includes(rt.id);
			const excluded = column.excludedFieldReportIds.includes(rt.id);
			return !hasSelection && !ignored && !excluded;
		});
	}, [familyChain, filterUnassigned, selectionsByRt, ignoreList, column.excludedFieldReportIds]);

	const handleFilterUnassigned = React.useCallback((_filterUnassigned: boolean) => () => {
		setFilterUnassigned(_filterUnassigned);
	}, []);

	const handleToogleIgnore = React.useCallback((rtId: number) => () => {
		onToggleIgnore(rtId, !ignoreList.includes(rtId));
	}, [ignoreList, onToggleIgnore]);

	if (!latestRt || !familyKey) {
		return null;
	}

	return (
		<CustomModal
			backdrop={false}
			closeModal={onClose}
			dialogClassName={styles.dialog}
			modalStyle="info"
			showModal={show}
			size="xxxl"
		>
			<CustomModal.Header
				closeModal={onClose}
				title={`${latestRt.name} Report Type Revisions – association with column ${column.name}`}
			/>
			<CustomModal.Body padding="none">
				<div className={styles.section}>
					<div className={styles['meta-field']}>
						<div className={styles['hint-title']}>
							Please select which field from each version of the Report Type
							{latestRt.name} will be associated with the column <b>{column.name}</b>.
						</div>
						<div className={styles['hint-sub']}>
							If you don’t wish to associate any fields from a specific revision select “Do not use this revision”.
						</div>
					</div>
					<div className={styles['report-type-filter']}>
						<div
							className={`${styles['filter-option']} ${filterUnassigned ? styles.active : ''
								}`}
							onClick={handleFilterUnassigned(true)}
						>
							Unassigned
						</div>
						<div
							className={`${styles['filter-option']} ${!filterUnassigned ? styles.active : ''
								}`}
							onClick={handleFilterUnassigned(false)}
						>
							Assigned
						</div>
					</div>
					<div
						className={`${styles['action-button']} ${isMarkAssignedAsDoNotUseDisabled ? styles['action-button--disabled'] : ''}`}
						onClick={isMarkAssignedAsDoNotUseDisabled ? undefined : handleMarkAssignedAsDoNotUse}
					>
						Mark all as Do not use
						<Tooltip message={TOOLTIP_MESSAGE}>
							<span className={`icon-info ${styles['info-icon']}`} />
						</Tooltip>
					</div>
				</div>
				<div className={styles['fields-pane']}>
					<div className={styles['picker-area']}>
						<div className={styles['picker-inner']}>
							<div className={styles['cards-scroll']}>
								<div className={styles['cards-grid']}>
									{filteredChain.map((reportType) => {

										return (
											<div
												className={`${styles['card-rt']} ${selectionsByRt.has(reportType.id) || ignoreList.includes(reportType.id)
													? styles.resolved
													: ''
													}`}
												key={reportType.id}
											>
												<div className={styles['card-header']}>
													<div className={styles['card-title']}>
														<div className={styles['card-name']}>
															<>{reportType.name} REVISION {revisionCode(reportType.revision)}</>
															{
																!selectionsByRt.has(reportType.id) && !ignoreList.includes(reportType.id) && (
																	<Tooltip message={TOOLTIP_MESSAGE_WARNING} >
																		<span
																			className={`${styles['card-warn-corner']} icon-warning`}
																		/>
																	</Tooltip>
																)
															}
														</div>
														{!selectionsByRt.has(reportType.id) && !ignoreList.includes(reportType.id) && (
															<span className={styles['card-meta']}>
																Missing association
															</span>
														)}
														<span className={styles['updated-at']}>
															Latest updated, {reportType.updatedAt}
														</span>
													</div>
													<Radio
														checked={ignoreList.includes(reportType.id)}
														disabled={false}
														label={'Do not use this revision'}
														onCheck={handleToogleIgnore(reportType.id)}
													/>
												</div>
												<div className={styles['card-body']}>
													{renderRevision(reportType)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			</CustomModal.Body>

			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<Button
					disabled={!canSave}
					label="Save"
					onClick={onSave}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(ReportTypeRevisionsModal);

